import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { Mail, ArrowLeft, Key, CheckCircle } from "lucide-react";

const ForgotPasswordPage = () => {
  const [step, setStep] = useState(1); // 1: Enter email, 2: Enter code & new password, 3: Success
  const [email, setEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [displayCode, setDisplayCode] = useState(""); // For demo purposes

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success("Reset code generated!");
      
      // For demo purposes, show the code (in production, this would be emailed)
      if (response.data.reset_code) {
        setDisplayCode(response.data.reset_code);
      }
      
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request password reset");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetCode) {
      toast.error("Please enter the reset code");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email,
        reset_code: resetCode,
        new_password: newPassword
      });
      toast.success("Password reset successfully!");
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-background to-primary/10 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-primary/20 shadow-xl">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
            {step === 3 ? (
              <CheckCircle className="h-8 w-8 text-primary-foreground" />
            ) : (
              <Key className="h-8 w-8 text-primary-foreground" />
            )}
          </div>
          <CardTitle className="font-heading text-2xl">
            {step === 1 && "Forgot Password"}
            {step === 2 && "Reset Password"}
            {step === 3 && "Password Reset!"}
          </CardTitle>
          <CardDescription>
            {step === 1 && "Enter your email to receive a reset code"}
            {step === 2 && "Enter the code and your new password"}
            {step === 3 && "Your password has been successfully reset"}
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-4">
          {step === 1 && (
            <form onSubmit={handleRequestReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-text-muted" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="pl-10"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-primary text-primary-foreground hover:bg-primary-hover"
                disabled={loading}
                data-testid="request-reset-btn"
              >
                {loading ? "Sending..." : "Send Reset Code"}
              </Button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                >
                  <ArrowLeft className="h-3 w-3" />
                  Back to Login
                </Link>
              </div>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              {/* Demo notice - showing the code */}
              {displayCode && (
                <div className="bg-accent/10 border border-accent/30 rounded-lg p-3 text-center">
                  <p className="text-xs text-text-secondary mb-1">Demo Mode - Your reset code is:</p>
                  <p className="font-mono font-bold text-2xl text-accent tracking-widest">{displayCode}</p>
                  <p className="text-xs text-text-muted mt-1">(In production, this would be sent to your email)</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="code">Reset Code</Label>
                <Input
                  id="code"
                  type="text"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  className="text-center font-mono text-lg tracking-widest"
                  maxLength={6}
                  data-testid="reset-code-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  data-testid="new-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-primary text-primary-foreground hover:bg-primary-hover"
                disabled={loading}
                data-testid="reset-password-btn"
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>

              <div className="flex justify-between text-sm">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-text-secondary hover:text-primary"
                >
                  ← Request new code
                </button>
                <Link to="/login" className="text-primary hover:underline">
                  Back to Login
                </Link>
              </div>
            </form>
          )}

          {step === 3 && (
            <div className="text-center space-y-4">
              <div className="bg-status-success/10 text-status-success rounded-lg p-4">
                <CheckCircle className="h-12 w-12 mx-auto mb-2" />
                <p className="font-medium">Your password has been reset!</p>
                <p className="text-sm mt-1">You can now log in with your new password.</p>
              </div>

              <Link to="/login">
                <Button className="w-full bg-primary text-primary-foreground hover:bg-primary-hover">
                  Go to Login
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
